import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Icon } from "@/components/icon";
import Logo from "@/components/logo";
import { NavVertical } from "@/components/nav";
import type { NavProps } from "@/components/nav/types";
import { GLOBAL_CONFIG } from "@/global-config";
import { useWalletAuth } from "@/routes/hooks/useWalletAuth";
import { Button } from "@/ui/button";
import { ScrollArea } from "@/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/ui/sheet";

export function NavMobileLayout({ data }: NavProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { connected, publicKey, isAuthenticated, connectAndAuthenticate, logout, isLoading } = useWalletAuth();
	const [dropdownVisible, setDropdownVisible] = useState(false);

	const handleSignIn = async () => {
		try {
			await connectAndAuthenticate();
		} catch (error) {
			console.error("Sign in failed:", error);
		}
	};

	const handleDisconnect = async () => {
		try {
			await logout();
		} catch (error) {
			console.error("Disconnect failed:", error);
		}
		setDropdownVisible(false);
	};

	// 关闭下拉菜单当点击外部时
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			if (dropdownVisible && !target.closest(".wallet-dropdown")) {
				setDropdownVisible(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [dropdownVisible]);

	return (
		<Sheet modal={false}>
			<button type="button" className="flex gap-2 px-2 h-[var(--layout-header-height)] items-center">
				<Logo />
				<span className="text-xl font-bold">{GLOBAL_CONFIG.appName}</span>
			</button>
			<SheetTrigger asChild>
				<Button variant="ghost" size="icon">
					<Icon icon="local:ic-menu" size={24} />
				</Button>
			</SheetTrigger>
			<SheetContent side="left" className="[&>button]:hidden px-2 w-[280px]">
				<div className="flex gap-2 px-2 h-[var(--layout-header-height)] items-center">
					<Logo />
					<span className="text-xl font-bold">{GLOBAL_CONFIG.appName}</span>
				</div>
				<ScrollArea className="h-full">
					<NavVertical data={data} />
				</ScrollArea>
				<div className="px-2 pb-4">
					<div className="flex flex-col items-center space-y-2">
						{!connected ? (
							<Button
								className="bg-[#F5F5F5] hover:bg-[#E5E5E5] text-[#666666] px-6 py-2.5 rounded-full font-medium transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed w-full"
								onClick={handleSignIn}
								disabled={isLoading}
							>
								{isLoading ? t("wallet.connecting") : t("wallet.signIn")}
							</Button>
						) : (
							<div className="relative wallet-dropdown w-full">
								<Button
									className="bg-[#F5F5F5] hover:bg-[#E5E5E5] text-[#666666] px-6 py-2.5 rounded-full font-medium transition-all duration-200 text-sm w-full"
									onClick={() => setDropdownVisible(!dropdownVisible)}
								>
									{publicKey && `${publicKey.toBase58().slice(0, 4)}...${publicKey.toBase58().slice(-4)}`}
								</Button>
								{dropdownVisible && (
									<div className="absolute right-0 bottom-full mb-2 w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl z-50 border border-gray-200 dark:border-gray-700 overflow-hidden">
										<button
											type="button"
											onClick={() => {
												navigate("/portfolio");
												setDropdownVisible(false);
											}}
											className="block w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
										>
											{t("wallet.profile")}
										</button>
										<button
											type="button"
											onClick={handleDisconnect}
											className="block w-full text-left px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 border-t border-gray-100 dark:border-gray-700"
										>
											{t("wallet.logout")}
										</button>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</SheetContent>
		</Sheet>
	);
}
